import { useCallback } from 'react';
import { DataTableFilterMeta, DataTableSortEvent, DataTableFilterEvent } from 'primereact/datatable';
import { usePersistentState } from './usePersistentState';

export interface UseDataTableStateOptions {
  /** Screen/tab identifier for storage key namespacing (e.g., 'dataReview:checks') */
  storageKey: string;
  /** Default sort configuration */
  defaultSort?: { field: string; order: 1 | -1 };
  /** Default column filter configuration (matches PrimeReact FilterMeta) */
  defaultFilters?: DataTableFilterMeta;
  /** Default values for custom filters (status checkboxes, file filter, etc.) */
  defaultCustomFilters?: Record<string, any>;
}

interface SortState {
  field: string | null;
  order: 1 | 0 | -1 | null | undefined;
}

export interface UseDataTableStateReturn {
  // Sort state
  sortField: string | null;
  sortOrder: 1 | 0 | -1 | null | undefined;
  handleSort: (event: DataTableSortEvent) => void;

  // Column filter state (DataTable built-in)
  filters: DataTableFilterMeta;
  setFilters: React.Dispatch<React.SetStateAction<DataTableFilterMeta>>;
  handleFilter: (event: DataTableFilterEvent) => void;

  // Custom filter state (status, file, date range)
  customFilters: Record<string, any>;
  setCustomFilter: (key: string, value: any) => void;
  setCustomFilters: React.Dispatch<React.SetStateAction<Record<string, any>>>;

  // Utilities
  resetFilters: () => void;
  resetSort: () => void;
  resetAll: () => void;
}

/**
 * A hook that manages DataTable filter and sort state with localStorage persistence.
 * Provides handlers for sort events, filter events, and custom filters.
 */
export function useDataTableState(options: UseDataTableStateOptions): UseDataTableStateReturn {
  const { storageKey, defaultSort, defaultFilters = {}, defaultCustomFilters = {} } = options;

  // Persist sort state
  const [sortState, setSortState] = usePersistentState<SortState>(
    `filters:${storageKey}:sort`,
    { field: defaultSort?.field ?? null, order: defaultSort?.order ?? null }
  );

  // Persist column filters - merge with defaults to pick up new filter keys
  const [storedFilters, setFilters] = usePersistentState<DataTableFilterMeta>(
    `filters:${storageKey}:columnFilters`,
    defaultFilters
  );
  // Merge defaults with stored: use stored value but always use default matchMode
  // This ensures matchMode changes in code take effect even with persisted filters
  const filters = Object.keys(defaultFilters).reduce((acc, key) => {
    const defaultFilter = defaultFilters[key];
    const storedFilter = storedFilters[key];
    if (defaultFilter && typeof defaultFilter === 'object' && 'matchMode' in defaultFilter) {
      // For standard filters, use stored value but default matchMode
      acc[key] = {
        ...defaultFilter,
        value: storedFilter && 'value' in storedFilter ? storedFilter.value : defaultFilter.value,
      };
    } else {
      // For other filter types, use stored or default
      acc[key] = storedFilter ?? defaultFilter;
    }
    return acc;
  }, {} as DataTableFilterMeta);

  // Persist custom filters (status checkboxes, file dropdown, etc.)
  const [storedCustomFilters, setCustomFiltersState] = usePersistentState<Record<string, any>>(
    `filters:${storageKey}:customFilters`,
    defaultCustomFilters
  );
  // Merge defaults with stored to ensure new filter keys exist
  const customFilters = { ...defaultCustomFilters, ...storedCustomFilters };

  const handleSort = useCallback((event: DataTableSortEvent) => {
    setSortState({
      field: event.sortField ?? null,
      order: event.sortOrder ?? null,
    });
  }, [setSortState]);

  const handleFilter = useCallback((event: DataTableFilterEvent) => {
    setFilters(event.filters);
  }, [setFilters]);

  const setCustomFilter = useCallback((key: string, value: any) => {
    setCustomFiltersState(prev => ({ ...prev, [key]: value }));
  }, [setCustomFiltersState]);

  const resetFilters = useCallback(() => {
    setFilters(defaultFilters);
    setCustomFiltersState(defaultCustomFilters);
  }, [defaultFilters, defaultCustomFilters, setFilters, setCustomFiltersState]);

  const resetSort = useCallback(() => {
    setSortState({ field: defaultSort?.field ?? null, order: defaultSort?.order ?? null });
  }, [setSortState, defaultSort]);

  const resetAll = useCallback(() => {
    resetFilters();
    resetSort();
  }, [resetFilters, resetSort]);

  return {
    sortField: sortState.field,
    sortOrder: sortState.order,
    handleSort,
    filters,
    setFilters,
    handleFilter,
    customFilters,
    setCustomFilter,
    setCustomFilters: setCustomFiltersState,
    resetFilters,
    resetSort,
    resetAll,
  };
}
